/**
 * Helpers to manage uploading files to be used for LLM contexts within the notebook.
 *
 * Problem:
 *  - User-uploaded files must be attached to the entry as a form of audit trail.
 *  - Attaching files to the entry is slow since backend generates PDF previews for most files at upload time.
 *
 * Solution:
 *  - Preemptively upload files to S3 and create NoteLinkedFile as soon as a user has selected them.
 *    The UI will be able to kick off the LLM operation as soon as the files are uploaded, and can
 *    block on the notebook uploads completing. In the happy path, the user does not have to wait
 *    at all as the LLM operation is slow.
 *
 * In the future, we may want first-class support for auditing via a dedicated domain model, which would
 * largely avoid the need for preemptive file uploads.
 */

import {useState, useMemo, useCallback} from 'react';

import * as NoteLinkedFileDAO from 'notebook/notes/attachments/NoteLinkedFileDAO';
import {FromServerNoteLinkedFile} from 'notebook/util/NoteUtilConstants';
import * as ImportUtil from 'util/ImportUtil';
import {getUserMessage} from 'util/Net';

import {AIModalSelectedFile} from '../AIModalFileChooser';
import {FillStructuredTableUsingAIFile} from '../NotebookAIToolsDAO';

interface FileUploadState {
  s3UploadPromise: Promise<string>;
  noteLinkedFilePromise: Promise<FromServerNoteLinkedFile>;
  errorMessage: string | null;
}

interface FileWithUploadState {
  file: AIModalSelectedFile;
  uploadState: FileUploadState;
}

/**
 * Convert a (deprecated) JQueryPromise to a Promise.
 */
async function asPromise<U>(jqPromise: JQueryPromise<U>): Promise<U> {
  // We use ! as JQueryPromise<U> has incorrect type stubs:
  //  - Incorrect: `JQueryPromise<U>.then((x: U | undefined) => ...)`
  //  - Correct:   `JQueryPromise<U>.then((x: U) => ...)`.
  return (await jqPromise)!;
}

/** @package exported for testing */
export function _formatErrorMessage(file: File, error: unknown): string {
  const base = `Failed to upload ${file.name}: ${getUserMessage(error) ?? 'An error occurred.'}`;
  return `${base}${base.endsWith('.') ? ' ' : '. '}Try removing the file and re-uploading it.`;
}

/**
 * Upload a file to S3 and create a NoteLinkedFile record for it.
 *
 * onError is called if the upload fails. FileUploadState['noteLinkedFilePromise'] will still be rejected.
 */
function startFileUpload(
  file: File,
  {onError}: {onError: (newState: FileUploadState) => void}
): FileUploadState {
  const s3UploadPromise = asPromise(ImportUtil.upload(file));
  const noteLinkedFilePromise = s3UploadPromise
    .then((s3Location) => asPromise(NoteLinkedFileDAO.create({location: s3Location})))
    .then((noteLinkedFile) => noteLinkedFile);

  const rv: FileUploadState = {s3UploadPromise, noteLinkedFilePromise, errorMessage: null};

  // NB: we do not add this to the promise chain above as that would cause the returned noteLinkedFilePromise
  // to not be rejected when the upload fails.
  noteLinkedFilePromise.catch((e) => {
    onError({...rv, errorMessage: _formatErrorMessage(file, e)});
  });

  return rv;
}

/**
 * Hook to manage uploading a single file. See module-level comment for more details.
 */
export function useFileUpload(): {
  /** Upload a file and then create a NoteLinkedFile */
  uploadFile: (file: File) => void;
  /**
   * Resolves to the NoteLinkedFile object for the uploaded file.
   * Rejects if uploadFile was not already called.
   */
  waitForNoteLinkedFile: () => Promise<FromServerNoteLinkedFile>;
  errorMessage: string | null;
} {
  const [uploadState, setUploadState] = useState<FileUploadState | null>(null);

  const uploadFile = useCallback((file: File) => {
    const uploadState = startFileUpload(file, {
      onError: (errorState) => {
        setUploadState(errorState);
      },
    });
    setUploadState(uploadState);
  }, []);

  const waitForNoteLinkedFile = useCallback(
    (): Promise<FromServerNoteLinkedFile> =>
      uploadState
        ? uploadState.noteLinkedFilePromise
        : Promise.reject(new Error('uploadFile must be called before waitForNoteLinkedFile')),
    [uploadState]
  );

  return {uploadFile, errorMessage: uploadState?.errorMessage ?? null, waitForNoteLinkedFile};
}

/**
 * Hook to manage selecting and uploading files. See module-level comment for more details.
 */
export function useFileUploads(): {
  selectedFiles: Array<AIModalSelectedFile>;
  /** Track selected files. Files will be immediately uploaded, and then processed into a NoteLinkedFile. */
  setSelectedFiles: (newFiles: Array<AIModalSelectedFile>) => void;
  /** Error message if any file upload / NoteLinkedFile creation fails. */
  errorMessage: string | null;
  /** Resolves to the S3-uploaded files (in temp bucket) */
  waitForFileUploads: () => Promise<Array<FillStructuredTableUsingAIFile>>;
  /** Resolves to the (final) NoteLinkedFile objects */
  waitForNoteLinkedFiles: () => Promise<Array<FromServerNoteLinkedFile>>;
} {
  const [filesWithUploadState, setFilesWithUploadState] = useState<Array<FileWithUploadState>>([]);

  const selectedFiles = useMemo(
    () => filesWithUploadState.map((state) => state.file),
    [filesWithUploadState]
  );

  const errorMessage = useMemo(
    () =>
      filesWithUploadState.find((state) => state.uploadState.errorMessage != null)?.uploadState
        .errorMessage ?? null,
    [filesWithUploadState]
  );

  const waitForFileUploads = useCallback(
    (): Promise<Array<FillStructuredTableUsingAIFile>> =>
      Promise.all(
        filesWithUploadState.map(
          async (state: FileWithUploadState): Promise<FillStructuredTableUsingAIFile> => ({
            s3Location: await state.uploadState.s3UploadPromise,
            mediaType: state.file.file.type,
          })
        )
      ),
    [filesWithUploadState]
  );

  const waitForNoteLinkedFiles = useCallback(
    (): Promise<Array<FromServerNoteLinkedFile>> =>
      Promise.all(filesWithUploadState.map(async (state) => await state.uploadState.noteLinkedFilePromise)),
    [filesWithUploadState]
  );

  const setSelectedFiles = useCallback((newFiles: Array<AIModalSelectedFile>) => {
    setFilesWithUploadState((prevStates) =>
      newFiles.map((file) => {
        // Reuse existing upload state if file hasn't changed
        const existingState = prevStates.find((state) => state.file.key === file.key);
        if (existingState) {
          return existingState;
        }
        // Otherwise, start a new upload...
        const uploadState = startFileUpload(file.file, {
          onError: (errorState) => {
            // Update `filesWithUploadState` to reflect a file upload error.
            setFilesWithUploadState((prevStates) =>
              prevStates.map((state) =>
                state.file.key === file.key ? {...state, uploadState: errorState} : state
              )
            );
          },
        });
        return {file, uploadState};
      })
    );
  }, []);

  return {
    selectedFiles,
    setSelectedFiles,
    waitForFileUploads,
    waitForNoteLinkedFiles,
    errorMessage,
  };
}
